import { useEffect, useRef, useState } from 'react';
import { log } from '@/shared/utils/logger';
import { projectViewPreferencesService } from '@/features/projects/services/projectViewPreferencesService';

export const GANTT_TREE_VIEW_KEY = 'gantt-tree';
export const GANTT_TREE_SAVE_DELAY_MS = 350;

const signatureOf = (expandedNodeIds: Iterable<string>, isCompactView: boolean) => JSON.stringify({
  expandedNodeIds: Array.from(new Set(expandedNodeIds)).sort(),
  isCompactView,
});

interface ServerGanttTreePreferencesOptions {
  projectId: string | null;
  expandedNodeIds: Set<string>;
  isCompactView: boolean;
  setExpandedNodeIds: React.Dispatch<React.SetStateAction<Set<string>>>;
  setIsCompactViewState: React.Dispatch<React.SetStateAction<boolean>>;
}

/**
 * Loads the per-project Gantt tree preference (expanded rows and compact mode) from the server when a single project
 * is visible, then saves later changes after a short delay. What the server returned is recorded as already saved,
 * so opening a project (in any view) never writes back the preference it has just read.
 */
export const useServerGanttTreePreferences = ({
  projectId,
  expandedNodeIds,
  isCompactView,
  setExpandedNodeIds,
  setIsCompactViewState,
}: ServerGanttTreePreferencesOptions): void => {
  const [isReady, setIsReady] = useState(false);
  const lastSavedRef = useRef('');

  useEffect(() => {
    lastSavedRef.current = '';
    setIsReady(false);
    if (!projectId) {
      return undefined;
    }

    let cancelled = false;
    projectViewPreferencesService.get(projectId, GANTT_TREE_VIEW_KEY)
      .then((preferences) => {
        if (cancelled) {
          return;
        }
        const storedExpanded = Array.isArray(preferences.expandedNodeIds)
          ? preferences.expandedNodeIds.filter((nodeId): nodeId is string => typeof nodeId === 'string')
          : null;
        if (storedExpanded) {
          setExpandedNodeIds(new Set(storedExpanded));
        }
        if (typeof preferences.isCompactView === 'boolean') {
          setIsCompactViewState(preferences.isCompactView);
        }
        if (storedExpanded && typeof preferences.isCompactView === 'boolean') {
          lastSavedRef.current = signatureOf(storedExpanded, preferences.isCompactView);
        }
      })
      .catch((error) => {
        log.warn('ProjectDataContext', 'Failed to load server Gantt tree preferences', { projectId, error });
      })
      .finally(() => {
        if (!cancelled) {
          setIsReady(true);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [projectId, setExpandedNodeIds, setIsCompactViewState]);

  useEffect(() => {
    if (!projectId || !isReady) {
      return undefined;
    }

    const signature = signatureOf(expandedNodeIds, isCompactView);
    if (lastSavedRef.current === signature) {
      return undefined;
    }

    const timeoutId = window.setTimeout(() => {
      projectViewPreferencesService.save(projectId, GANTT_TREE_VIEW_KEY, JSON.parse(signature))
        .then(() => {
          lastSavedRef.current = signature;
        })
        .catch((error) => {
          log.warn('ProjectDataContext', 'Failed to save server Gantt tree preferences', { projectId, error });
        });
    }, GANTT_TREE_SAVE_DELAY_MS);

    return () => window.clearTimeout(timeoutId);
  }, [expandedNodeIds, isCompactView, isReady, projectId]);
};
